"use client"

import { ReportScope } from "@ibada/shared-types/reporting"
import type { ReportDefinition, ReportEntityRegistry, ReportExportType, ReportResult, SavedReportRecord } from "@ibada/shared-types/reporting"

import { http } from "@/shared/lib/api/http"
import type {
  ReportExportInput,
  ReportRunResult,
  ReportTemplateRunInput,
  ReportingTemplateListResult,
  ReportingTemplateSummary,
  ReportsRouteScope,
  SaveReportInput,
} from "@/features/reports/types/reports-types"

type ApiEnvelope<TData> = {
  data: TData
  meta: Record<string, unknown>
}

type ReportEntityListEnvelope = ApiEnvelope<{
  items: ReportEntityRegistry[]
}>

type ReportTemplateListEnvelope = ApiEnvelope<{
  items: ReportingTemplateSummary[]
}>

type SavedReportListEnvelope = ApiEnvelope<{
  items: SavedReportRecord[]
}>

type SavedReportEnvelope = ApiEnvelope<SavedReportRecord>

type ReportResultEnvelope = ApiEnvelope<ReportResult>

/**
 * The scope a catalog request is made under.
 *
 * Only a BRANCH request names a branch — the same rule the run path follows. Sending `branchId` at PARISH
 * scope makes the server's scope resolution fail, and because these endpoints answer a failed resolution
 * with an EMPTY LIST rather than an error, it failed silently: 200 with zero templates.
 *
 * That is why nothing at parish scope was ever locked or filtered. The catalog fell back to its static
 * registry and looked fine, so the bug only surfaced once the list was used to decide what a caller may
 * actually see.
 */
function buildScopeQueryParams(scope: ReportsRouteScope) {
  return {
    workspaceId: scope.workspaceId,
    scopeKind: scope.scopeKind,
    branchId: scope.scopeKind === ReportScope.BRANCH ? scope.branchId : undefined,
  }
}

async function listEntities(scope: ReportsRouteScope): Promise<ReportEntityRegistry[]> {
  const response = await http.get<ReportEntityListEnvelope>("/reports/entities", {
    params: buildScopeQueryParams(scope),
  })

  return response.data.data.items
}

async function listTemplates(scope: ReportsRouteScope): Promise<ReportingTemplateListResult["items"]> {
  const response = await http.get<ReportTemplateListEnvelope>("/reports/templates", {
    params: buildScopeQueryParams(scope),
  })

  return response.data.data.items
}

async function listSaved(scope: ReportsRouteScope): Promise<SavedReportRecord[]> {
  const response = await http.get<SavedReportListEnvelope>("/reports/saved", {
    params: {
      workspaceId: scope.workspaceId,
      // Branch context so the server can include branch-public reports of the current branch.
      branchId: scope.branchId,
    },
  })

  return response.data.data.items
}

export const reportsApi = {
  listEntities,
  listTemplates,
  listSaved,
  getSaved: async (workspaceId: string, id: string): Promise<SavedReportRecord> => {
    const response = await http.get<SavedReportEnvelope>(`/reports/saved/${encodeURIComponent(id)}`, {
      params: { workspaceId },
    })

    return response.data.data
  },
  preview: async (definition: ReportDefinition): Promise<ReportRunResult> => {
    const response = await http.post<ReportResultEnvelope>("/reports/preview", {
      definition,
    })

    return response.data.data
  },
  run: async (definition: ReportDefinition): Promise<ReportRunResult> => {
    const response = await http.post<ReportResultEnvelope>("/reports/run", {
      definition,
    })

    return response.data.data
  },
  runTemplate: async (input: ReportTemplateRunInput): Promise<ReportRunResult> => {
    const response = await http.post<ReportResultEnvelope>(
      `/reports/templates/${encodeURIComponent(input.templateId)}/run`,
      {
        scope: input.scope,
        inputs: input.inputs,
      },
    )

    return response.data.data
  },
  /** Resolve a template + inputs to an editable {@link ReportDefinition} (opens it in the builder, doesn't run it). */
  resolveTemplateDefinition: async (input: ReportTemplateRunInput): Promise<ReportDefinition> => {
    const response = await http.post<{ data: { definition: ReportDefinition }; meta: unknown }>(
      `/reports/templates/${encodeURIComponent(input.templateId)}/definition`,
      {
        scope: input.scope,
        inputs: input.inputs,
      },
    )

    return response.data.data.definition
  },
  createSaved: async (input: SaveReportInput): Promise<SavedReportRecord> => {
    const response = await http.post<SavedReportEnvelope>("/reports/saved", {
      ...input,
    })

    return response.data.data
  },
  updateSaved: async (workspaceId: string, id: string, input: SaveReportInput): Promise<SavedReportRecord> => {
    const response = await http.put<SavedReportEnvelope>(
      `/reports/saved/${encodeURIComponent(id)}`,
      {
        ...input,
      },
      {
        params: { workspaceId },
      },
    )

    return response.data.data
  },
  deleteSaved: async (workspaceId: string, id: string): Promise<void> => {
    await http.delete(`/reports/saved/${encodeURIComponent(id)}`, {
      params: { workspaceId },
    })
  },
  runSaved: async (workspaceId: string, id: string): Promise<ReportRunResult> => {
    const response = await http.post<ReportResultEnvelope>(
      `/reports/saved/${encodeURIComponent(id)}/run`,
      undefined,
      {
        params: { workspaceId },
      },
    )

    return response.data.data
  },
  exportReport: async (input: ReportExportInput): Promise<{ blob: Blob; fileName: string | null; exportType: ReportExportType }> => {
    const response = await http.post<ArrayBuffer>("/reports/export", input, {
      responseType: "arraybuffer",
    })

    const contentDisposition = response.headers["content-disposition"]
    const fileNameMatch = typeof contentDisposition === "string" ? /filename="?([^"]+)"?/i.exec(contentDisposition) : null
    const contentTypeHeader = response.headers["content-type"]
    const contentType = typeof contentTypeHeader === "string" ? contentTypeHeader : "application/octet-stream"

    return {
      blob: new Blob([response.data], {
        type: contentType,
      }),
      fileName: fileNameMatch?.[1] ?? null,
      exportType: input.exportType,
    }
  },
  /**
   * Renders an already-formatted template report as a PDF.
   *
   * The rows travel as display text — the surface formatted them against the reader's locale and the
   * report's currency, and the server re-formats nothing. What the server does NOT take from here is the
   * document's identity: workspace, branch, logo and who generated it are resolved from the caller's own
   * authorization, so a header cannot be forged by asking nicely.
   */
  renderTemplatePdf: async (input: ReportTemplatePdfInput): Promise<Blob> => {
    const response = await http.post<ArrayBuffer>("/reports/templates/pdf", input, {
      responseType: "arraybuffer",
    })
    const contentTypeHeader = response.headers["content-type"]
    return new Blob([response.data], {
      type: typeof contentTypeHeader === "string" ? contentTypeHeader : "application/pdf",
    })
  },
}

export type ReportTemplatePdfInput = {
  workspaceId: string
  branchId?: string | null
  title: string
  filters?: Array<{ label: string; value: string }>
  summary?: Array<{ label: string; value: string }>
  columns: Array<{ key: string; label: string; alignment?: "left" | "center" | "right" }>
  rows: Array<Record<string, string>>
}
